import React, { useState } from "react";
import styled from "styled-components";
import SearchIcon from "./icon/SearchIcon";
import RateIcon from "./icon/RateIcon";
import { transferToNative } from "@src/lib/WebAppBridge";
import { getMyADCountRequest, useCancelItemAD, useGetMyADCount } from "../service/ad.api";
import dayjs from "dayjs";
import ConfirmModal from "@src/components/popup/ConfirmModal";
import convertNumberToKorean from "@src/util/convertNumToKorean";

const ItemADList = () => {
    const [requestData, setRequestData] = useState<getMyADCountRequest>({});
    const [isOpenCancelModal, setIsOpenCancelModal] = useState<{ state: boolean; bannerNo: number | null }>({
        state: false,
        bannerNo: null,
    });

    const { data: myADCount, refetch: refetchMyADCount } = useGetMyADCount(requestData);
    const { mutate: cancelItemAD } = useCancelItemAD();

    const handleClickItem = (itemId: string) => {
        transferToNative(itemId, "goItemDetail");
    };

    return (
        <Layout>
            {/* 광고 종료 모달 */}
            {isOpenCancelModal.state && (
                <ConfirmModal
                    onConfirm={() => {
                        cancelItemAD(isOpenCancelModal.bannerNo!);
                        setIsOpenCancelModal({ state: false, bannerNo: null });
                        refetchMyADCount();
                    }}
                    resultMessage={
                        <CancelADModal>
                            <div className="title">광고를 종료하시겠습니까?</div>
                            <div className="content">
                                광고를 종료하면 매물 상세 또는 지도에서
                                <br />
                                더 이상 노출되지 않으며,
                                <br />
                                <br />
                                결제 금액은 환불되지 않습니다.
                            </div>
                        </CancelADModal>
                    }
                    cancelText="취소"
                    onCancel={() => setIsOpenCancelModal({ state: false, bannerNo: null })}
                />
            )}

            <Selector>
                <div
                    className={`selectorItem ${requestData.adStatus === undefined ? "active" : ""}`}
                    onClick={() => {
                        setRequestData({});
                    }}
                >
                    전체({myADCount?.countInfo?.totalCount.toLocaleString()})
                </div>
                <div
                    className={`selectorItem ${requestData.adStatus === "normal" ? "active" : ""}`}
                    onClick={() => {
                        setRequestData({ adStatus: "normal" });
                    }}
                >
                    광고중({myADCount?.countInfo?.normalCount.toLocaleString()})
                </div>
                <div
                    className={`selectorItem ${requestData.adStatus === "closed" ? "active" : ""}`}
                    onClick={() => {
                        setRequestData({ adStatus: "closed" });
                    }}
                >
                    광고종료({myADCount?.countInfo?.closedCount.toLocaleString()})
                </div>
            </Selector>

            <Counter>
                총{" "}
                {requestData.adStatus === "normal"
                    ? myADCount?.countInfo?.normalCount.toLocaleString()
                    : requestData.adStatus === "closed"
                    ? myADCount?.countInfo?.closedCount.toLocaleString()
                    : myADCount?.countInfo?.totalCount.toLocaleString()}
                건
            </Counter>

            <SearchBox>
                <div className="InputBox">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="매물 검색"
                        value={requestData.caseId}
                        onChange={(e) => setRequestData({ ...requestData, caseId: e.target.value })}
                    />
                </div>
            </SearchBox>

            <ItemList>
                {myADCount?.list.map((item, idx) => {
                    const today = dayjs().startOf("day");
                    const expiredDate = dayjs(item.expiredAt).startOf("day");
                    const diff = expiredDate.diff(today, "day");

                    const dDayText = diff > 0 ? `D-${diff}` : diff === 0 ? "D-Day" : "마감";

                    return (
                        <Item key={idx}>
                            <div className="DayInfoBox">
                                <div className="StatusBox">
                                    <div className="status" style={{ color: item.bannerStatus !== 200 ? "#8D8D8D" : "#0047FF" }}>
                                        {item.bannerStatus !== 200 ? "광고종료" : "광고중"}
                                    </div>
                                    <div className="date">
                                        {item.startedAt} ~ {item.expiredAt}
                                    </div>
                                </div>

                                {/* <div
                                    className="endBtn"
                                    onClick={() => setIsOpenCancelModal({ state: true, bannerNo: item.bannerNo })}
                                >
                                    광고종료
                                </div> */}
                            </div>

                            <div className="ItemBox" onClick={() => handleClickItem(item.itemId)}>
                                <div className="imgBox">
                                    <img src={item.image} alt="" />
                                    <div className="dayBadge">{dDayText}</div>
                                </div>

                                <div className="ItmeInfo">
                                    <div className="Info1">경매 {item.itemSubId}</div>
                                    <div className="Info2">
                                        {item.court} {item.bidDate}
                                    </div>
                                    <div className="Info3">{item.use}</div>
                                    <div className="Info4">{item.address}</div>
                                    <div className="Info5">
                                        <div className="label">최저가</div>
                                        <div className="value">{convertNumberToKorean(item.minPrice)}</div>
                                        <div className="discount">
                                            (<RateIcon location="down" /> {item.discountRate}%)
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Item>
                    );
                })}
            </ItemList>
        </Layout>
    );
};

const Layout = styled.div``;

const Selector = styled.div`
    width: 100%;

    display: flex;
    align-items: center;
    justify-content: center;

    .selectorItem {
        width: 100%;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 14px;

        border-bottom: 4px solid #dadada;

        color: #0b1024;
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;

        &.active {
            border-bottom: 4px solid #0047ff;
            color: #0064ff;
            font-weight: 700;
        }
    }
`;

const Counter = styled.div`
    padding: 4px 14px;
    margin-top: 8px;

    color: #000;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
`;

const SearchBox = styled.div`
    padding: 8px 14px;

    .InputBox {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;

        border-radius: 100px;
        background: #fff;
        box-shadow: 1px 2px 2px 0 rgba(4, 0, 14, 0.36);

        input {
            width: 100%;
            border: none;
            outline: none;

            color: #0b1024;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;

            &::placeholder {
                color: #505050;
                font-size: 14px;
                font-weight: 400;
                line-height: 24px;
            }
        }
    }
`;

const ItemList = styled.div`
    width: 100%;
    height: calc(100vh - 280px);
    overflow-y: scroll;
    margin-top: 6px;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const Item = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;

    .DayInfoBox {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        .StatusBox {
            display: flex;
            flex-direction: row;
            gap: 4px;

            .status {
                color: #0047ff;
                font-size: 13px;
                font-weight: 700;
                line-height: 20px;
            }

            .date {
                color: #8d8d8d;
                font-size: 12px;
                font-weight: 400;
                line-height: 20px;
            }
        }

        .endBtn {
            padding: 2px 8px;

            border-radius: 4px;
            background: #f0f0f0;

            color: #8d8d8d;
            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
        }
    }

    .ItemBox {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;

        .imgBox {
            position: relative;
            width: 100px;
            height: 100px;

            img {
                width: 100%;
                height: 100%;
                border-radius: 4px;
                filter: blur(1px);
            }

            .dayBadge {
                position: absolute;
                top: 12px;
                left: 12px;

                color: #eee562;
                font-size: 20px;
                font-weight: 700;
                line-height: 28px;
            }
        }
    }

    .ItmeInfo {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .Info1 {
            color: #000;
            font-size: 14px;
            font-weight: 700;
            line-height: 24px;
        }

        .Info2 {
            color: #8d8d8d;
            font-size: 12px;
            font-weight: 400;
            line-height: 12px;
        }

        .Info3 {
            color: #0c002c;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
        }

        .Info4 {
            width: 240px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            color: #505050;
            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
        }

        .Info5 {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 4px;

            .label {
                color: #0047ff;
                font-size: 14px;
                font-weight: 400;

                &.up {
                    color: #f00;
                }
            }

            .value {
                color: #0c002c;
                font-size: 14px;
                font-weight: 400;
            }

            .discount {
                color: #0047ff;
                font-size: 13px;
                font-weight: 400;

                &.up {
                    color: #f00;
                }
            }
        }
    }
`;

const CancelADModal = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;

    .title {
        color: #0c002c;
        font-size: 16px;
        font-weight: 700;
        line-height: 26px;
    }

    .content {
        color: #0c002c;
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;
    }
`;

export default ItemADList;
